import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag,
  Space, Popconfirm, message, Card, Typography, Badge
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getBlacklist, addBlacklist, deleteBlacklist } from '../services/api';

const { Text } = Typography;

const typeConfig = {
  ip: { color: 'blue', label: 'IP' },
  asn: { color: 'orange', label: 'ASN' },
  fingerprint: { color: 'purple', label: '指纹' },
  canvas: { color: 'magenta', label: 'Canvas' },
  webrtc_ip: { color: 'volcano', label: 'WebRTC IP' },
  user_agent: { color: 'cyan', label: 'UA' },
};

function BlacklistPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [page, pageSize, typeFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (typeFilter) params.type = typeFilter;
      const res = await getBlacklist(params);
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (values) => {
    setSubmitting(true);
    try {
      await addBlacklist(values);
      message.success('添加成功');
      setModalVisible(false);
      form.resetFields();
      loadData();
    } catch (err) {
      message.error(err.response?.data?.error || '添加失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteBlacklist(id);
      message.success('已删除');
      loadData();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (val) => {
        const cfg = typeConfig[val] || { color: 'default', label: val };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      }
    },
    {
      title: '值',
      dataIndex: 'value',
      key: 'value',
      render: (val) => <Text code style={{ fontSize: 12 }}>{val}</Text>
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (val) => <Text type="secondary" style={{ fontSize: 12 }}>{val || '-'}</Text>
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (val) => <Text type="secondary" style={{ fontSize: 11 }}>{val}</Text>
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="确认删除此规则？"
          onConfirm={() => handleDelete(record.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="text" danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ];

  return (
    <div>
      <Card size="small" bordered={false} style={{ marginBottom: 12, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Select
              allowClear
              placeholder="全部类型"
              style={{ width: 130 }}
              size="small"
              value={typeFilter}
              onChange={(val) => { setTypeFilter(val); setPage(1); }}
            >
              <Select.Option value="ip">IP</Select.Option>
              <Select.Option value="asn">ASN</Select.Option>
              <Select.Option value="fingerprint">指纹</Select.Option>
              <Select.Option value="canvas">Canvas</Select.Option>
              <Select.Option value="webrtc_ip">WebRTC IP</Select.Option>
              <Select.Option value="user_agent">User-Agent</Select.Option>
            </Select>
            <Badge count={total} style={{ backgroundColor: '#1890ff' }} overflowCount={9999}>
              <Text type="secondary" style={{ fontSize: 12, paddingRight: 8 }}>规则</Text>
            </Badge>
          </Space>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            添加规则
          </Button>
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          size: 'small',
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); }
        }}
      />

      <Modal
        title="添加黑名单规则"
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={420}
      >
        <Form form={form} layout="vertical" onFinish={handleAdd} style={{ marginTop: 16 }}>
          <Form.Item
            name="type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select placeholder="选择类型">
              <Select.Option value="ip">IP 地址</Select.Option>
              <Select.Option value="asn">ASN 编号</Select.Option>
              <Select.Option value="fingerprint">浏览器指纹</Select.Option>
              <Select.Option value="canvas">Canvas 指纹</Select.Option>
              <Select.Option value="webrtc_ip">WebRTC IP</Select.Option>
              <Select.Option value="user_agent">User-Agent</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="value"
            label="值"
            rules={[{ required: true, message: '请输入值' }]}
          >
            <Input placeholder="如: 1.2.3.4 / 13335 / abc123..." />
          </Form.Item>
          <Form.Item name="reason" label="原因（可选）">
            <Input.TextArea placeholder="备注拉黑原因..." rows={2} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              确认添加
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BlacklistPage;
